/**
 * Patient-preparation text ("indicaciones") of several studies, combined into one coherent list
 * (pilot report 2026-11-03: a quote for BHC + EGO + QS6 said "NO HAY INDICACIONES PREVIAS y AYUNO
 * DE 8 - 12 HRS", because each study's text was simply joined).
 *
 * Pure and shared by the quote WhatsApp message, the quote ticket and the order ticket. It never
 * invents text: what the laboratory typed in Catálogo is all that comes out.
 */

const fold = (s: string) =>
  s
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLocaleLowerCase("es-MX")
    .replace(/\s+/g, " ")
    .trim();

// "no hay indicaciones previas", "sin indicaciones", "ninguna", "no requiere preparación"…
// Anything that also mentions fasting or a sample is a real indication, never a "none".
const NONE_BARE = /^(ninguna?|n\/a|no aplica|no|sin nada)$/;
const NONE_PHRASE =
  /^(no (hay|existen|existe|requiere|se requiere|tiene|aplica)|sin|ninguna?)\s+([a-z]+\s+){0,2}?(indicaciones?|preparacion|requisitos?)(\s+(previas?|especiales|especificas?|particulares))?$/;

/** True for a text that says there is nothing to prepare ("NO HAY INDICACIONES PREVIAS."). */
export function isNoIndication(text: string): boolean {
  const key = fold(text).replace(/[\s.;,:]+$/, "");
  return NONE_BARE.test(key) || NONE_PHRASE.test(key);
}

/**
 * Conflict groups: indications that contradict each other when several studies are combined
 * (pilot report 2026-11-03, quote CVC-000005: "AYUNO DE 8 - 12 HRS." next to "NO REQUIERE AYUNO.",
 * "PRIMERA ORINA DE LA MAÑANA." next to "ORINA OCASIONAL."). Data-driven: `tiers` run from the
 * weakest to the strongest; once any line of a higher tier is present, the lower tiers of the
 * same group are dropped. `compare`, when given, orders lines of the same (top) tier by
 * demand; only the most demanding survives, unless a line is not comparable (then all stay).
 * Patterns run against the folded text (lowercase, no accents). Lines are printed as typed.
 */
interface ConflictGroup {
  name: string;
  tiers: RegExp[];
  compare?: (folded: string) => number | null;
}

/** "ayuno de 8 - 12 hrs" gives 12 (the upper bound is the most demanding), "ayuno de 4 horas" gives 4. */
const fastingHours = (f: string): number | null => {
  const m = /(\d+(?:[.,]\d+)?)(?:\s*(?:-|a)\s*(\d+(?:[.,]\d+)?))?\s*(?:h|hr|hrs|hora|horas)\b/.exec(f);
  return m ? Number((m[2] ?? m[1])!.replace(",", ".")) : null;
};

export const CONFLICT_GROUPS: readonly ConflictGroup[] = [
  {
    name: "fasting",
    tiers: [
      // "no requiere ayuno", "sin ayuno" (a bare statement, nothing more)
      /^(no (requiere|necesita|se requiere|es necesario|hay) (el )?ayuno|sin ayuno)( previo)?$/,
      // any other mention of fasting ("ayuno de 8 - 12 hrs")
      /\bayuno\b/,
    ],
    compare: fastingHours,
  },
  {
    name: "urine sample",
    tiers: [
      // a random sample is also satisfied by a first-morning one
      /^(muestra de )?orina (ocasional|al azar|aleatoria|simple)$/,
      /\b(primera|primer) (orina|miccion)\b|\borina (de la )?(primera hora|matutina)\b|\borina de la manana\b/,
    ],
    // "orina de 24 horas" matches no tier: a different sample that stays alongside.
  },
];

function resolveConflicts(parts: string[]): string[] {
  const drop = new Set<number>();
  for (const g of CONFLICT_GROUPS) {
    // The tier of each part: the first matching one, weakest first (the weak patterns are the
    // specific ones; the strong patterns are broad and would also match "sin ayuno").
    const tiers = parts.map((p) => {
      const f = fold(p).replace(/[\s.;,:]+$/, "");
      for (let t = 0; t < g.tiers.length; t++) if (g.tiers[t]!.test(f)) return { t, f };
      return null;
    });
    const top = Math.max(-1, ...tiers.map((x) => x?.t ?? -1));
    if (top < 0) continue;
    const kept: number[] = [];
    tiers.forEach((x, i) => {
      if (!x) return;
      if (x.t < top) drop.add(i);
      else kept.push(i);
    });
    if (g.compare && kept.length > 1) {
      const vals = kept.map((i) => g.compare!(tiers[i]!.f));
      if (vals.every((v) => v !== null)) {
        const best = Math.max(...(vals as number[]));
        const winner = kept.find((_, k) => vals[k] === best)!;
        for (const i of kept) if (i !== winner) drop.add(i);
      }
    }
  }
  return parts.filter((_, i) => !drop.has(i));
}

export interface IndicationSummary {
  /** The distinct real indications, in order, in the wording first typed (sentence by sentence). */
  parts: string[];
  /** Only when `parts` is empty and some study said "no indications": that wording, once. */
  none: string | null;
}

/**
 * Collect, normalise and combine: split each text into sentences, drop the "no indications"
 * ones whenever at least one real indication exists, remove duplicates (case-, accent- and
 * trailing-period-insensitive; the first wording wins). If every study says none, say it once.
 */
export function summariseIndications(texts: readonly (string | null | undefined)[]): IndicationSummary {
  const seen = new Set<string>();
  const parts: string[] = [];
  let none: string | null = null;
  for (const text of texts) {
    for (const raw of (text ?? "").split(/\n+|;\s*|\.(?:\s+|$)/)) {
      const part = raw.trim().replace(/\s+/g, " ");
      if (!part) continue;
      if (isNoIndication(part)) {
        none ??= part;
        continue;
      }
      const key = fold(part);
      if (seen.has(key)) continue;
      seen.add(key);
      parts.push(part);
    }
  }
  const resolved = resolveConflicts(parts);
  return { parts: resolved, none: resolved.length ? null : none };
}

/**
 * The summary as printable sentences (each ends with a period, as the laboratory usually types
 * them): the real indications, else the single "no indications" wording, else nothing.
 */
export function indicationLines(texts: readonly (string | null | undefined)[]): string[] {
  const s = summariseIndications(texts);
  const lines = s.parts.length ? s.parts : s.none ? [s.none] : [];
  return lines.map((l) => (/[.!?:]$/.test(l) ? l : `${l}.`));
}
